import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTA } from "@/components/CTA";
import { Partners } from "@/components/Partners";
import { ClientLogos } from "@/components/ClientLogos";
import { ToolsUsedByWavai } from "@/components/ToolsUsedByWavai";
import { WhyWavai } from "@/components/WhyWavai";
import { Users, Award, Globe, Zap, Quote } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: "easeOut" as const } },
};

const About = () => {
  const { lang } = useLanguage();
  const t = (en: string, ar?: string) => {
    if (lang === "ar" && ar) return ar;
    return getTranslation(en, lang);
  };

  const { canonical, hreflang } = buildHreflang("/about");
  useCmsPageSEO({
    title: lang === "ar" ? "عن وافاي | وكالة الويب الموثوقة في الكويت" : "About Wavai | Kuwait's Trusted Web Agency",
    description: lang === "ar"
      ? "تعرّف على وافاي — وكالة كويتية للويب والتجارة الإلكترونية والذكاء الاصطناعي تساعد العلامات التجارية في دول الخليج على النمو بتصميم متميز وكود نظيف ونتائج قابلة للقياس."
      : "Meet Wavai — a Kuwait-based web, eCommerce and AI agency helping brands across the GCC scale with premium design, clean code and measurable growth.",
    keywords: "about Wavai, Kuwait digital agency, Shopify partner Kuwait, ecommerce agency Kuwait, GCC web agency",
    canonical,
    hreflang,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Wavai",
      url: "https://www.wavai.ae",
      logo: "https://www.wavai.ae/logo-wavai.svg",
      foundingLocation: { "@type": "Place", name: "UAE" },
      areaServed: ["UAE", "Kuwait", "GCC"],
    },
  });

  const { data: testimonials } = useQuery({
    queryKey: ["testimonials-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .eq("status", "published")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  const stats = [
    { value: "50+", label: t("Projects Delivered", "مشروع منجز") },
    { value: "5.0", label: t("Shopify Rating", "تقييم شوبيفاي") },
    { value: "10+", label: t("Countries Served", "دول نخدمها") },
    { value: "2015", label: t("Partner Since", "شريك منذ") },
  ];

  const values = [
    {
      icon: Users,
      title: t("Human-First Approach", "نهج يركّز على الإنسان"),
      description: t(
        "We blend AI-driven efficiency with human creativity to deliver digital solutions that resonate with real users.",
        "نجمع بين كفاءة الذكاء الاصطناعي والإبداع البشري لتقديم حلول رقمية تلامس المستخدمين الحقيقيين."
      ),
    },
    {
      icon: Award,
      title: t("Excellence in Execution", "التميّز في التنفيذ"),
      description: t(
        "Every project is delivered with precision, attention to detail, and a relentless focus on quality.",
        "كل مشروع يُنجز بدقة واهتمام بالتفاصيل وتركيز مستمر على الجودة."
      ),
    },
    {
      icon: Globe,
      title: t("Global Perspective", "رؤية عالمية"),
      description: t(
        "From the UAE to the world, we understand diverse markets and help brands scale internationally.",
        "من الإمارات إلى العالم، نفهم الأسواق المتنوعة ونساعد العلامات التجارية على التوسع دوليًا."
      ),
    },
    {
      icon: Zap,
      title: t("AI-Powered Innovation", "ابتكار مدعوم بالذكاء الاصطناعي"),
      description: t(
        "We leverage cutting-edge AI, automation, and no-code tools to accelerate your digital growth.",
        "نستخدم أحدث أدوات الذكاء الاصطناعي والأتمتة وأدوات بدون كود لتسريع نموك الرقمي."
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 lg:pt-40 lg:pb-32 relative">
          <div className="absolute inset-0 bg-gradient-hero opacity-40" />
          <motion.div
            className="container px-6 relative z-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" as const }}
          >
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm uppercase tracking-wider text-primary font-medium mb-4">
                {t("About Wavai", "عن Wavai")}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
                {t("Your Digital Innovation", "شريكك في الابتكار")}
                <span className="text-gradient"> {t("Growth Partner", "الرقمي والنمو")}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t(
                  "Wavai is a digital innovation agency based in the Kuwait, specializing in AI solutions, SaaS development, eCommerce platforms, automation, and no-code technology. We help businesses build scalable digital experiences that drive measurable growth.",
                  "Wavai هي وكالة ابتكار رقمي مقرّها الكويت، متخصصة في حلول الذكاء الاصطناعي، وتطوير منصات SaaS، ومنصات التجارة الإلكترونية، والأتمتة، وتقنيات بدون كود. نساعد الشركات على بناء تجارب رقمية قابلة للتوسع تحقق نموًا ملموسًا."
                )}
              </p>
            </div>
          </motion.div>
        </section>

        {/* Stats */}
        <section className="py-16 border-y border-border">
          <div className="container px-6">
            <motion.div
              className="grid grid-cols-2 lg:grid-cols-4 gap-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
            >
              {stats.map((stat) => (
                <motion.div key={stat.label} variants={fadeUp} className="text-center">
                  <div className="text-4xl lg:text-5xl font-display font-bold text-gradient mb-2">
                    {stat.value}
                  </div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <ClientLogos />

        {/* Story Section */}
        <section className="py-12 lg:py-32">
          <div className="container px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease: "easeOut" as const }}
              >
                <p className="text-sm uppercase tracking-wider text-primary font-medium mb-4">
                  {t("Our Story", "قصتنا")}
                </p>
                <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
                  {t("Built for the Future of Digital", "مصممة لمستقبل العالم الرقمي")}
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    {t(
                      "Founded in the Kuwait, Wavai was born from a vision to bridge the gap between traditional business operations and the new era of AI-powered digital transformation.",
                      "تأسست Wavai في الكويت انطلاقًا من رؤية لسد الفجوة بين العمليات التجارية التقليدية والعصر الجديد للتحول الرقمي المدعوم بالذكاء الاصطناعي."
                    )}
                  </p>
                  <p>
                    {t(
                      "We recognized that businesses need more than just websites. They need intelligent, scalable platforms that combine eCommerce, SaaS, AI automation, and no-code solutions to stay competitive in a rapidly evolving market.",
                      "أدركنا أن الشركات تحتاج إلى أكثر من مجرد مواقع إلكترونية. فهي بحاجة إلى منصات ذكية وقابلة للتوسع تجمع بين التجارة الإلكترونية وحلول SaaS وأتمتة الذكاء الاصطناعي وأدوات بدون كود للبقاء في المنافسة."
                    )}
                  </p>
                  <p>
                    {t(
                      "Today, we serve clients across the GCC, Middle East, and globally, helping them launch, grow, and scale their digital businesses with the perfect blend of human creativity and AI precision.",
                      "اليوم، نخدم عملاء في دول الخليج والشرق الأوسط وحول العالم، نساعدهم على إطلاق أعمالهم الرقمية وتنميتها وتوسيعها بمزيج مثالي من الإبداع البشري ودقة الذكاء الاصطناعي."
                    )}
                  </p>
                </div>
              </motion.div>
              <motion.div
                className="grid grid-cols-2 gap-4"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
              >
                {values.map((value) => (
                  <motion.div
                    key={value.title}
                    variants={scaleIn}
                    whileHover={{ y: -4 }}
                    className="p-6 rounded-2xl bg-card border border-border shadow-card transition-shadow hover:shadow-glow"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <value.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-sans font-medium mb-2">{value.title}</h3>
                    <p className="text-sm text-muted-foreground">{value.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials Section — Dynamic */}
        {testimonials && testimonials.length > 0 && (
          <section className="py-12 lg:py-32 bg-surface">
            <div className="container px-6">
              <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-sm uppercase tracking-wider text-primary font-medium mb-4">
                  {t("Testimonials", "آراء العملاء")}
                </p>
                <h2 className="text-3xl sm:text-4xl font-display font-bold">
                  {t("What Our Clients Say", "ماذا يقول عملاؤنا")}
                </h2>
              </motion.div>

              <motion.div
                className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.1 }}
              >
                {testimonials.map((item: any) => (
                  <motion.div
                    key={item.id}
                    variants={fadeUp}
                    whileHover={{ y: -4 }}
                    className="relative p-8 rounded-2xl bg-card border border-border shadow-card transition-shadow hover:shadow-glow flex flex-col justify-between"
                  >
                    <Quote className="h-8 w-8 text-primary/15 absolute top-6 right-6" />
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      "{lang === "ar" && item.quote_ar ? item.quote_ar : item.quote}"
                    </p>
                    <div className="flex items-center gap-4">
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <p className="font-semibold text-foreground">
                          {lang === "ar" && item.name_ar ? item.name_ar : item.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {lang === "ar" && item.role_ar ? item.role_ar : item.role}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
        )}

        <WhyWavai />
        <ToolsUsedByWavai />
        <Partners />
        <CTA />
      </main>
      <Footer />
    </div>
  );
};

export default About;
